/**
 * src/hooks/useUsers.ts
 * Manages user accounts list and user mutations
 */

import { useState, useEffect, useCallback } from 'react';
import { api, type UserItem } from '../services/api';

export const useUsers = () => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.users.list();
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const createUser = async (data: Parameters<typeof api.users.create>[0]) => {
    const res = await api.users.create(data);
    fetchUsers();
    return res;
  };

  const updateUser = async (id: number, data: Parameters<typeof api.users.update>[1]) => {
    const res = await api.users.update(id, data);
    fetchUsers();
    return res;
  };

  return {
    users,
    isLoading,
    error,
    refetch: fetchUsers,
    createUser,
    updateUser
  };
};
